
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { apiService } from '@/services/api';
import { queryKeys } from '@/lib/queryClient';
import { Plan } from '@/types/api';
import { useCallback } from 'react';

/**
 * Fetch all available plans (public data, no auth required)
 * Cached for 10 minutes since plans don't change frequently
 */
export function usePublicPlansQuery() {
  return useQuery({
    queryKey: queryKeys.plans,
    queryFn: () => apiService.fetchPlans(),
    staleTime: 1000 * 60 * 10, // 10 minutes - plans change rarely
    gcTime: 1000 * 60 * 30, // 30 minutes cache time
  });
}

/**
 * Fetch the current user's active plan
 * Optimized with 5-minute caching to reduce unnecessary API calls
 */
export function useUserPlanQuery() {
  const { getToken, isSignedIn, isLoaded } = useAuth();

  return useQuery({
    queryKey: queryKeys.userPlan,
    queryFn: async () => {
      console.log('🚀 Fetching user plan data from API');
      const token = await getToken();
      if (!token) {
        throw new Error('Authentication required');
      }
      const result = await apiService.fetchUserPlan(token);
      console.log('✅ User plan data received:', result?.name || 'null');
      return result;
    },
    enabled: isLoaded && isSignedIn, // Only run when user is authenticated
    staleTime: 1000 * 60 * 5, // 5 minutes - user plan doesn't change frequently
    gcTime: 1000 * 60 * 30, // 30 minutes cache time
    refetchOnWindowFocus: false, // Don't refetch on window focus to reduce calls
    refetchOnReconnect: true, // Refetch when network reconnects
    // Remove automatic polling - only refetch when explicitly needed
    retry: (failureCount, error) => {
      // Don't retry if user is not found or not authenticated
      if (error instanceof Error && (
        error.message.includes('Authentication') ||
        error.message.includes('404')
      )) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

/**
 * Combined hook that provides both plans and user plan data
 * This replaces the old usePlans hook with optimized caching
 */
export function usePlansData() {
  const queryClient = useQueryClient();
  const plansQuery = usePublicPlansQuery();
  const userPlanQuery = useUserPlanQuery();

  // Helper function to filter plans by provider
  const getFilteredPlans = useCallback((allPlans: Plan[], provider?: "stripe" | "clerk") => {
    if (!provider) {
      return allPlans;
    }
    return allPlans.filter(plan => plan.provider === provider);
  }, []);

  // Function to manually refresh user plan data
  const refreshUserPlan = useCallback(async () => {
    const result = await userPlanQuery.refetch();
    return result;
  }, [userPlanQuery]);

  // Function to manually refresh all plans data
  const refreshPlans = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.plans });
  }, [queryClient]);

  return {
    // Plans data
    plans: plansQuery.data || [],
    plansLoading: plansQuery.isLoading,
    plansError: plansQuery.error?.message || null,
    
    // User plan data
    activePlan: userPlanQuery.data || null,
    userPlanLoading: userPlanQuery.isLoading,
    userPlanError: userPlanQuery.error?.message || null,
    
    // Combined loading state
    isLoading: plansQuery.isLoading || userPlanQuery.isLoading,
    
    // Combined error state
    error: plansQuery.error?.message || userPlanQuery.error?.message || null,
    
    // Helper functions
    getFilteredPlans,
    refreshUserPlan,
    refreshPlans,
    
    // For backward compatibility
    fetchPlans: refreshPlans,
    fetchUserPlan: refreshUserPlan,
  };
}